import { Record, List, fromJS } from 'immutable';
import _ from 'lodash';

import { DeedType } from 'generated/graphql';
import getDeedPathSlugFromType from 'utils/getDeedPathSlugFromType';
import { Features } from 'utils/features';

import User, { FullName } from '../user/model';
import Organization from '../organization/model';
import Donation from '../donation/model';
import LocationLatLng from '../locationLatLng/model';
import LocationObject from '../locationObject/model';

import DeedRole from './role';

interface AmountCurrencies {
  [currencyCode: string]: number;
}

interface Hours {
  min: number;
  max: number;
}

interface Duration {
  min: number;
  max: number;
  unit: string;
}

interface ExternalLinkFollow {
  activityDates: Date[];
  id: string;
  user: User;
}

interface Editor {
  email: string;
  features: Record<Partial<Features>, boolean>;
  fullName: FullName;
  id: string;
  name: string;
  profile: {
    name: string;
    role: string;
  };
  token: {
    _id: string;
    role: string;
  };
}

const properties = {
  id: '',
  externalId: '',
  name: '',
  startingAt: {},
  createdAt: {},
  endingAt: {},
  duration: 0,
  hours: {},
  value: 0,
  organization: new Organization(),
  provisionalNonprofitData: undefined,
  partnerName: '',
  partnerEmail: '',
  partnerEin: '',
  partnerPhone: '',
  partnerLocation: '',
  partner: new Organization(),
  shareLink: '',
  duties: '',
  mustKnows: '',
  isPrivate: false,
  locationLatLng: new LocationLatLng(),
  locationObject: new LocationObject(),
  location: '',
  pictures: List(),
  mainPicture: '',
  attendeeLimit: 0,
  waitlistLimit: undefined,
  registrationEndingAt: undefined,
  attendees: [],
  waitlist: [],
  invites: [],
  checkIns: [],
  donationText: '',
  donationLink: '',
  backgroundCheckLink: '',
  volunteerFormLink: '',
  organizerName: '',
  organizerEmail: '',
  organizerPhone: '',
  campaigns: List(),
  pillars: List(),
  ERGs: List(),
  categories: List(),
  nonprofits: List(),
  SDGs: List(),
  formQuestions: List(),
  formResponses: List(),
  responsesAggregation: [],
  forms: List(),
  started: false,
  ended: false,
  public: false,
  featured: false,
  manual: false,
  timeZone: '',
  type: '',
  currencyCode: 'USD',
  goalAmount: 0,
  goalAmountCurrencies: {},
  donatedAmount: 0,
  donatedAmountCurrencies: {},
  matchedAmount: 0,
  matchedAmountCurrencies: {},
  description: '',
  virtual: false,
  linkOnly: false,
  requireAuthentication: false,
  skipApplicationStatusUpdates: false,
  milestones: List(),
  skills: List(),
  roles: List(),
  donations: List(),
  donationsCount: 0,
  status: 'draft',
  externalLink: '',
  allowedLocations: List(),
  externalLinkFollows: List(),
  externalLinkFollowsCount: 0,
  submitter: null,
  editors: [],
  // donationMatchActivated: false,
  // donationMatchPercentage: 0,
  // donationMatchLimit: 0,
  provider: undefined,
  userCount: 0,
  communityIds: List(),
  organizingCommunityIds: List(),
  isSharedWithSubsidiary: false,
  optedIn: false,
  companySubsidiaryAccess: false,
  sharedWithCompanies: List(),
  optedInCompanies: List(),
  shareFormResponses: false,
};

export default class Deed extends Record(properties, 'Deed') {
  public readonly id!: string;

  public readonly externalId!: string;

  public readonly type!: DeedType;

  public readonly name!: string;

  public readonly startingAt!: Date;

  public readonly createdAt!: Date;

  public readonly endingAt!: Date;

  public readonly duration!: number | Duration;

  public readonly hours!: Hours;

  public readonly value!: number;

  public readonly organization!: Organization;

  public readonly provisionalNonprofitData!: Partial<Organization>;

  public readonly partnerName!: string;

  public readonly partnerEmail!: string;

  public readonly partnerEin!: string;

  public readonly partnerPhone!: string;

  public readonly partnerLocation!: string;

  public readonly partner!: Organization;

  public readonly shareLink!: string;

  public readonly duties!: string;

  public readonly mustKnows!: string;

  public readonly isPrivate!: boolean;

  public readonly locationLatLng!: LocationLatLng;

  public readonly locationObject!: LocationObject;

  public readonly location!: string;

  public readonly pictures!: List<string>;

  public readonly mainPicture!: string;

  public readonly attendeeLimit!: number;

  public readonly waitlistLimit!: number;

  public readonly registrationEndingAt!: Date;

  public readonly attendees!: any[];

  public readonly waitlist!: any[];

  public readonly invites!: any[];

  public readonly checkIns!: string[];

  public readonly donationText!: string;

  public readonly donationLink!: string;

  public readonly backgroundCheckLink!: string;

  public readonly volunteerFormLink!: string;

  public readonly organizerName!: string;

  public readonly organizerEmail!: string;

  public readonly organizerPhone!: string;

  public readonly campaigns!: List<string>;

  public readonly pillars!: List<string>;

  public readonly ERGs!: List<string>;

  public readonly categories!: List<string>;

  public readonly nonprofits!: List<Organization>;

  public readonly SDGs!: List<string>;

  public readonly formQuestions!: List<any>;

  public readonly formResponses!: List<any>;

  public readonly responsesAggregation!: any[];

  public readonly forms!: List<any>;

  public readonly started!: boolean;

  public readonly ended!: boolean;

  public readonly public!: boolean;

  public readonly manual!: boolean;

  public readonly timeZone!: string;

  public readonly currencyCode!: string;

  public readonly goalAmount!: number;

  public readonly goalAmountCurrencies!: AmountCurrencies;

  public readonly donatedAmount!: number;

  public readonly donatedAmountCurrencies!: AmountCurrencies;

  public readonly matchedAmount!: number;

  public readonly matchedAmountCurrencies!: AmountCurrencies;

  public readonly description!: string;

  public readonly virtual!: boolean;

  public readonly linkOnly!: boolean;

  public readonly requireAuthentication!: boolean;

  public readonly skipApplicationStatusUpdates!: boolean;

  public readonly milestones!: List<any>;

  public readonly skills!: List<any>;

  public readonly roles!: List<DeedRole>;

  public readonly donations!: List<Donation>;

  public readonly donationsCount!: number;

  public readonly status!: string;

  public readonly externalLink!: string;

  public readonly allowedLocations!: List<string>;

  public readonly externalLinkFollows!: List<ExternalLinkFollow>;

  public readonly externalLinkFollowsCount!: number;

  public readonly submitter!: User;

  public readonly editors!: Editor[];

  public readonly provider!: string;

  public readonly userCount!: number;

  public readonly communityIds!: List<string>;

  public readonly organizingCommunityIds!: List<string>;

  public readonly isSharedWithSubsidiary!: boolean;

  public readonly optedIn!: boolean;

  public readonly companySubsidiaryAccess!: boolean;

  public readonly sharedWithCompanies!: List<Organization>;

  public readonly optedInCompanies!: List<Organization>;

  public readonly shareFormResponses!: boolean;

  constructor(values: any = {}) {
    const locationObject = new LocationObject(values.locationObject);
    const validValues = _.pick(values, Object.keys(properties));
    super({
      ...validValues,
      attendees: validValues.attendees || [],
      milestones: fromJS(values.milestones),
      roles: List(values.roles?.map((role) => new DeedRole(role)) ?? []),
      campaigns: List(values.campaigns),
      pillars: List(values.pillars),
      ERGs: List(values.ERGs),
      communityIds: List(values.communityIds || []),
      organizingCommunityIds: List(values.organizingCommunityIds || []),
      categories: List(values.categories),
      nonprofits: fromJS(values.nonprofits),
      SDGs: List(values.SDGs),
      forms: List(values.forms),
      pictures: List(values.pictures),
      allowedLocations:
        values.allowedLocations?.length > 0 ? List(values.allowedLocations) : undefined,
      startingAt: values.startingAt && new Date(values.startingAt),
      endingAt: values.endingAt && new Date(values.endingAt),
      createdAt: values.createdAt ? new Date(values.createdAt) : undefined,
      type: values.type || values.__t, // eslint-disable-line no-underscore-dangle
      organization:
        values.organization &&
        new Organization(
          typeof values.organization === 'string'
            ? { id: values.organization }
            : values.organization
        ),
      provisionalNonprofitData:
        values.provisionalNonprofitData && new Organization(values.provisionalNonprofitData),
      partner:
        values.partner &&
        new Organization(
          typeof values.partner === 'string' ? { id: values.partner } : values.partner
        ),
      locationLatLng: new LocationLatLng(values.location),
      locationObject,
      location: String(locationObject),
      formQuestions: fromJS(values.formQuestions),
      formResponses: fromJS(values.formResponses),
      donations: fromJS(
        values.donations?.map((donation: Donation) => {
          // eslint-disable-next-line no-param-reassign
          donation.currency = donation?.currency?.toUpperCase();
          return donation;
        })
      ),
      sharedWithCompanies: List(values.sharedWithCompanies),
      externalLinkFollows: List(values.externalLinkFollows || []),
      optedInCompanies: List(values.optedInCompanies),
      submitter: values.submitter
        ? new User({ ...values.submitter, __t: values.submitter.type })
        : null,
    });
  }

  public get pathSlug(): string {
    return getDeedPathSlugFromType(this.type);
  }

  public isUserAttending(user: string | User): boolean {
    const userId = typeof user === 'string' ? user : user?.id;
    return !!this.attendees.find(
      (attendee: User) => (typeof attendee === 'string' ? attendee : attendee.id) === userId
    );
  }

  public isUserWaitlisted(user: string | User): boolean {
    const userId = typeof user === 'string' ? user : user?.id;
    return !!this.waitlist.find(
      (waitlisted: any) => (typeof waitlisted === 'string' ? waitlisted : waitlisted.id) === userId
    );
  }

  // Get Total number of applicants/approved/rejected
  public getNumberOfUsersByRoleAndStatus(state = 'applicants'): number {
    return (
      this.roles?.reduce(
        (previousValue, currentRole) => previousValue + currentRole?.get(state)?.size,
        0
      ) || 0
    );
  }

  public hasUserAppliedForRole(user: string | User, roleId: string): boolean {
    const userId = typeof user === 'string' ? user : user?.id;
    if (roleId) {
      const role = this.roles.find((roleObject: any) => roleObject.get('id') === roleId);
      return role
        .get('applicants')
        .concat(role.get('approved'), role.get('rejected'))
        .includes(userId);
    }
    return this.roles
      ?.flatMap((role) =>
        role?.get('applicants').concat(role.get('approved'), role.get('rejected'))
      )
      .includes(userId);
  }

  get externalSource(): string {
    return this.externalId?.match(/ext-(.*)-/)[1];
  }

  // eslint-disable-next-line @typescript-eslint/no-dupe-class-members
  get timeZone() {
    const originalTimeZone = this.get('timeZone');
    if (this.virtual && !originalTimeZone) {
      return Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;
    }

    return originalTimeZone;
  }

  get isEventType(): boolean {
    return ['Event', 'BaseEvent'].includes(this.type);
  }

  get isExternalDeed(): boolean {
    return Boolean(!this.isEventType && this.externalId);
  }
}
